import { useState } from 'react';
import { useFieldArray, Control, Controller, UseFormRegister, UseFormSetValue, UseFormGetValues, DeepMap, FieldError, UseFormClearErrors, WatchObserver, UseFormWatch } from "react-hook-form";
import { Accordion, Card } from "react-bootstrap";
import { Trash } from "react-bootstrap-icons"
import CameraComponent from '../../CameraComponent'
import CreateMembershipCommand from '../../../models/membership/CreateMembershipCommand';
import { ErrorMessage } from '@hookform/error-message';
import { SamplesAcquired } from '@digitalpersona/devices';
import FingerprintReader, { b64UrlTob64 } from '../../FingerprintReader'
import { DefaultMemberViewModel } from '../../../models/member/MemberViewModel';
import DeleteMemberCommand from '../../../models/member/DeleteMemberCommand';
import MemberController from '../../../controllers/MemberController';

interface IProperties {
    control: Control<any>;
    register: UseFormRegister<any>;
    setValue: UseFormSetValue<any>;
    getValues: UseFormGetValues<any>;
    errors: DeepMap<CreateMembershipCommand, FieldError>;
    clearErrors: UseFormClearErrors<any>;
    hasMultipleMembers: boolean;
    watch: UseFormWatch<any>;
    isEditing: boolean;
}

export default function MemberCard({ control, register, setValue, getValues, errors, clearErrors, hasMultipleMembers, watch, isEditing }: IProperties) {
    const { fields, append, remove, prepend } = useFieldArray({
        control,
        name: "Membership.Members"
    });
    const [numberOfFingerprints, setNumberOfFingerprints] = useState(0);
    var index = 0;

    const copyFromPreviousMember = (index: number) => {
        setValue(`Membership.Members.${index}.Email`, getValues(`Membership.Members.${index - 1}.Email`));
        setValue(`Membership.Members.${index}.PhoneNumber`, getValues(`Membership.Members.${index - 1}.PhoneNumber`));
        setValue(`Membership.Members.${index}.Address1`, getValues(`Membership.Members.${index - 1}.Address1`));
        setValue(`Membership.Members.${index}.Address2`, getValues(`Membership.Members.${index - 1}.Address2`));
        setValue(`Membership.Members.${index}.Town`, getValues(`Membership.Members.${index - 1}.Town`));
        setValue(`Membership.Members.${index}.Postcode`, getValues(`Membership.Members.${index - 1}.Postcode`));
    }
    const memberController = new MemberController();

    const sampleAcquired = (sample: SamplesAcquired) => {
        console.log(numberOfFingerprints)
        var data = b64UrlTob64(sample.samples[0].Data.toString());
        try {
            var fingerprints = getValues(`Membership.Members.${index}.Thumbprints`);
            if (!fingerprints || fingerprints.filter((fingerprint: string) => fingerprint).length == 0) {
                fingerprints.length = 0;
                fingerprints = [];
            }
            if (fingerprints.filter((fingerprint: string) => fingerprint).length >= 4) {
                fingerprintReader.stopAcquiring();
                return;
            }
            clearErrors(`Membership.Members.${index}.Thumbprints`);
            fingerprints = fingerprints.filter((fingerprint: string) => fingerprint);
            fingerprints.push(data);
            setValue(`Membership.Members.${index}.Thumbprints`, fingerprints);
            setNumberOfFingerprints(fingerprints.length);
            if (fingerprints.filter((fingerprint: string) => fingerprint).length >= 4) {
                fingerprintReader.stopAcquiring();
            }
        }
        catch (err) {
            console.log(err)
        }
    }

    const deleteMember = async (memberId: string) => {
        var result = window.confirm("This will permantantly delete this member and all associated data. Are you sure?");
        if (result) {
            var model : DeleteMemberCommand = {
                MemberId: memberId
            } 
            var result = await memberController.DeleteMember(model);
            if(result){
                location.reload();
            }
        }
    }

    const fingerprintReader = new FingerprintReader();

    const beginAcquisition = (memberIndex: number) => {
        index = memberIndex;
        setNumberOfFingerprints(0);
        fingerprintReader.samplesAcquired = (sample: SamplesAcquired) => { sampleAcquired(sample) };
        fingerprintReader.startAcquiring();
    }

    const clearFingerprints = (memberIndex: number) => {
        setValue(`Membership.Members.${memberIndex}.Thumbprints`, []);
        setValue(`Membership.Members.${memberIndex}.Thumbprints.0`, null);
        setValue(`Membership.Members.${memberIndex}.Thumbprints.1`, null);
        setValue(`Membership.Members.${memberIndex}.Thumbprints.2`, null);
        setValue(`Membership.Members.${memberIndex}.Thumbprints.3`, null);
        setNumberOfFingerprints(0);
    }

    return (
        <>
            {fields.map((item, number) => {
                index = number;
                return (
                    <Card className="mb-3" key={item.id}>
                        <Accordion.Toggle className="text-dark" as={Card.Header} eventKey={number.toString()}>
                            New Member {(number + 1).toString()}
                            {number > 0 && !getValues(`Membership.Members.${number}.Id`) &&
                                <Trash className="float-end" size={24} onClick={() => remove(number)} />
                            }
                        </Accordion.Toggle>
                        <Accordion.Collapse className="bg-dark" eventKey={number.toString()}>
                            <Card.Body>
                                <div className="row">
                                    <div className="col">
                                        <div className="form-floating mb-3">
                                            <input {...register(`Membership.Members.${number}.Firstname`)} defaultValue={watch(`Membership.Members.${number}.Firstname`)} type="text" className="form-control" placeholder="First Name" />
                                            <label htmlFor="floatingInput" className="text-dark">First Name</label>
                                            <p className="text-danger"><ErrorMessage errors={errors} name={`Membership.Members.${number}.Firstname`} /></p>
                                        </div>
                                    </div>
                                    <div className="col">
                                        <div className="form-floating mb-3">
                                            <input {...register(`Membership.Members.${number}.Surname`)} defaultValue={watch(`Membership.Members.${number}.Surname`)} type="text" className="form-control" placeholder="Last Name" />
                                            <label htmlFor="floatingInput" className="text-dark">Last Name</label>
                                            <p className="text-danger"><ErrorMessage errors={errors} name={`Membership.Members.${number}.Surname`} /></p>
                                        </div>
                                    </div>
                                </div>
                                {index != 0 &&
                                    <div className="form-floating mb-3">
                                        <button type="button" className="btn btn-lg btn-light" onClick={() => copyFromPreviousMember(index)}>Copy Data</button>
                                    </div>
                                }
                                <div className="row">
                                    <div className="col">
                                        <div className="form-floating mb-3">
                                            <input {...register(`Membership.Members.${number}.Email`)} defaultValue={watch(`Membership.Members.${number}.Email`)} type="text" className="form-control" placeholder="Email" />
                                            <label htmlFor="floatingInput" className="text-dark">Email</label>
                                            <p className="text-danger"><ErrorMessage errors={errors} name={`Membership.Members.${number}.Email`} /></p>
                                        </div>
                                    </div>
                                    <div className="col">
                                        <div className="form-floating mb-3">
                                            <input {...register(`Membership.Members.${number}.PhoneNumber`)} defaultValue={watch(`Membership.Members.${number}.PhoneNumber`)} type="text" className="form-control" placeholder="Phone Number" />
                                            <label htmlFor="floatingInput" className="text-dark">Phone Number</label>
                                            <p className="text-danger"><ErrorMessage errors={errors} name={`Membership.Members.${number}.PhoneNumber`} /></p>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-6">
                                        <div className="form-floating mb-3">
                                            <input {...register(`Membership.Members.${number}.DateOfBirth`, { required: "Date of Birth required" })} defaultValue={watch(`Membership.Members.${number}.DateOfBirth`)} type="date" className="form-control" placeholder="Date of Birth" />
                                            <label htmlFor="floatingInput" className="text-dark">Date of Birth</label>
                                            <p className="text-danger"><ErrorMessage errors={errors} name={`Membership.Members.${number}.DateOfBirth`} /></p>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-6">
                                        <div className="row">
                                            <div className="col">
                                                <div className="form-floating mb-3">
                                                    <input {...register(`Membership.Members.${number}.Address1`)} defaultValue={watch(`Membership.Members.${number}.Address1`)} type="text" className="form-control" placeholder="Address 1" />
                                                    <label htmlFor="floatingInput" className="text-dark">Address 1</label>
                                                    <p className="text-danger"><ErrorMessage errors={errors} name={`Membership.Members.${number}.Address1`} /></p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="form-floating mb-3">
                                                <input {...register(`Membership.Members.${number}.Address2`)} defaultValue={watch(`Membership.Members.${number}.Address2`)} type="text" className="form-control" placeholder="Address 2" />
                                                <label htmlFor="floatingInput" className="text-dark">Address 2</label>
                                                <p className="text-danger"><ErrorMessage errors={errors} name={`Membership.Members.${number}.Address2`} /></p>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col">
                                                <div className="form-floating mb-3">
                                                    <input {...register(`Membership.Members.${number}.Town`)} defaultValue={watch(`Membership.Members.${number}.Town`)} type="text" className="form-control" placeholder="Town" />
                                                    <label htmlFor="floatingInput" className="text-dark">Town</label>
                                                    <p className="text-danger"><ErrorMessage errors={errors} name={`Membership.Members.${number}.Town`} /></p>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <div className="form-floating mb-3">
                                                    <input {...register(`Membership.Members.${number}.Postcode`)} defaultValue={watch(`Membership.Members.${number}.Postcode`)} type="text" className="form-control" placeholder="Postcode" />
                                                    <label htmlFor="floatingInput" className="text-dark">Postcode</label>
                                                    <p className="text-danger"><ErrorMessage errors={errors} name={`Membership.Members.${number}.Postcode`} /></p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <Controller control={control}
                                        name={`Membership.Members.${number}.AddressPicture`}
                                        defaultValue={watch(`Membership.Members.${number}.AddressPicture`)}
                                        render={({ field: { onChange } }) => (
                                            <CameraComponent image={null} imageUpdated={onChange} />

                                        )} />
                                </div>
                                <div className="row">
                                    <div className="col-6 text-center">
                                            <>
                                                {getValues(`Membership.Members.${number}.Thumbprints`) &&
                                                    <h2>{4 - getValues(`Membership.Members.${number}.Thumbprints`).filter((fingerprint: string) => fingerprint).length} more fingerprints required</h2>}
                                                {!getValues(`Membership.Members.${number}.Thumbprints`) &&
                                                    <h2>4 more fingerprints required</h2>}
                                                {(!getValues(`Membership.Members.${number}.Thumbprints`) || getValues(`Membership.Members.${number}.Thumbprints`).filter((fingerprint: string) => fingerprint).length == 0) &&
                                                    <button type="button" className="btn btn-lg btn-light" onClick={() => beginAcquisition(number)}>Capture Fingerprints</button>
                                                }
                                                {(!getValues(`Membership.Members.${number}.Thumbprints`) || getValues(`Membership.Members.${number}.Thumbprints`).filter((fingerprint: string) => fingerprint).length != 0) &&
                                                    <button type="button" className="btn btn-lg btn-light" onClick={() => clearFingerprints(number)}>Clear Fingerprints</button>
                                                }
                                            </>                                        
                                        <br /><p className="text-danger"><ErrorMessage errors={errors} name={`Membership.Members.${number}.Thumbprints`} /></p>

                                    </div>
                                    <div className="col-6">
                                        <div className="mb-3 form-check">
                                            <input {...register(`Membership.Members.${number}.DataProtectionAgreed`)} defaultChecked={watch(`Membership.Members.${number}.DataProtectionAgreed`)} type="checkbox" className="form-check-input" />
                                            <label className="form-check-label">Data protection policy accepted?</label>
                                            <br /><p className="text-danger"><ErrorMessage errors={errors} name={`Membership.Members.${number}.DataProtectionAgreed`} /></p>
                                        </div>
                                        <div className="mb-3 form-check">
                                            <input {...register(`Membership.Members.${number}.HoldInformationAgreed`)} defaultChecked={watch(`Membership.Members.${number}.HoldInformationAgreed`)} type="checkbox" className="form-check-input" />
                                            <label className="form-check-label">Consent to hold information given?</label>
                                            <br /><p className="text-danger"><ErrorMessage errors={errors} name={`Membership.Members.${number}.HoldInformationAgreed`} /></p>
                                        </div>
                                        <div className="mb-3 form-check">
                                            <input {...register(`Membership.Members.${number}.MarketingOptIn`)} defaultChecked={watch(`Membership.Members.${number}.MarketingOptIn`)} type="checkbox" className="form-check-input" />
                                            <label className="form-check-label">Happy to recieve marketing information?</label>
                                            <br /><p className="text-danger"><ErrorMessage errors={errors} name={`Membership.Members.${number}.MarketingOptIn`} /></p>
                                        </div>
                                        {isEditing && getValues(`Membership.Members.${number}.Id`) &&
                                            <div className="mb-3 form-check">
                                                <input {...register(`Membership.Members.${number}.IsDeleted`)} defaultChecked={watch(`Membership.Members.${number}.IsDeleted`)} type="checkbox" className="form-check-input" />
                                                <label className="form-check-label">Deactivate member?</label>
                                                <br /><p className="text-danger"><ErrorMessage errors={errors} name={`Membership.Members.${number}.IsDeleted`} /></p>
                                            </div>
                                        }
                                    </div>
                                </div>
                                {isEditing &&
                                <div className="col-12">                                    
                                    <button type="button" className="btn btn-lg btn-danger" onClick={async () => {deleteMember(getValues(`Membership.Members.${number}.Id`))}}>Delete Member</button>
                                </div>
                                }
                            </Card.Body>
                        </Accordion.Collapse>
                    </Card>)
            })}

            {hasMultipleMembers &&
                <button className="btn btn-lg btn-light float-start" type="button" onClick={() => {
                    var members = getValues("Membership.Members");
                    append(DefaultMemberViewModel());
                    setValue(`Membership.Members.${members.length}.Thumbprints`, []);
                }}>Add Person</button>
            }
        </>
    );
}